<template>
  <section class="flow-section">
    <div class="container text-center">

      <h2 class="fw-bold mb-5 heading-glow">
        Nuestro Proceso <span>Transparente</span>
      </h2>

      <div class="steps-container">
        <div class="steps-line">
          
          <div class="progress-track"></div>

          <div class="progress-fill" :style="{ width: progressWidth }"></div>

          <div
            v-for="(step, index) in steps"
            :key="index"
            class="step-circle"
            :class="{ 
              'active': currentStep >= index, 
              'current': currentStep === index 
            }"
            @click="setStep(index)"
          >
            {{ index + 1 }}
          </div>
        </div>
      </div>

      <div class="card-display-area">
        <transition name="fade-slide" mode="out-in">
          <div :key="currentStep" class="single-card">
            <i class="bi bi-stars text-info fs-4 mb-3 d-block"></i>
            
            <h3 class="card-title">{{ steps[currentStep].title }}</h3>
            <p class="card-text">{{ steps[currentStep].description }}</p>
          </div>
        </transition>
      </div>

    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue"

// DATOS DE LOS PASOS
const steps = [
  {
    title: "Descubrimiento",
    description: "Profundizamos en tus necesidades, entendemos tu negocio y definimos la ruta estratégica del proyecto.",
  },
  {
    title: "Diseño",
    description: "Creamos prototipos, definimos la arquitectura y aseguramos la mejor experiencia de usuario antes del desarrollo.",
  },
  {
    title: "Desarrollo",
    description: "Construcción sólida con estándares premium, seguridad, pruebas continuas y entregas incrementales.",
  },
  {
    title: "Lanzamiento",
    description: "Publicamos tu solución con despliegue profesional y acompañamiento post–producción.",
  },
]

// ESTADO
const currentStep = ref(0)
let timer = null
const stepDuration = 3500 // 3.5 segundos por paso

// CÁLCULO DE LA BARRA DE PROGRESO (0% -> 33% -> 66% -> 100%)
const progressWidth = computed(() => {
  return (currentStep.value / (steps.length - 1)) * 100 + "%"
})

// LÓGICA DEL TEMPORIZADOR
const startTimer = () => {
  stopTimer() // Limpia cualquier timer previo para evitar conflictos
  timer = setInterval(() => {
    // Si llega al final, vuelve a 0, si no, suma 1
    currentStep.value = currentStep.value === steps.length - 1 ? 0 : currentStep.value + 1
  }, stepDuration)
}

const stopTimer = () => {
  if (timer) clearInterval(timer)
}

// INTERACCIÓN DEL USUARIO
const setStep = (index) => {
  currentStep.value = index
  startTimer() // Reinicia la cuenta regresiva al hacer clic manual
}

// CICLO DE VIDA DEL COMPONENTE
onMounted(() => {
  startTimer()
})

onUnmounted(() => {
  stopTimer()
})
</script>

<style scoped>
/* =========================================
   1. SECCIÓN PRINCIPAL & TIPOGRAFÍA
   ========================================= */
.flow-section {
  background: #1f3c4d; /* Fondo oscuro base */
  padding: 2rem 3rem;
  color: white;
  font-family: "Satoshi", sans-serif; /* Tu fuente preferida */
  overflow: hidden; /* Evita scrolls accidentales */
}

.heading-glow {
  font-size: 2.5rem;
  color: white;
}
.heading-glow span {
  color: #78ffd6;
  text-shadow: 0 0 15px rgba(120, 255, 214, 0.5); /* Resplandor neón */
}

/* =========================================
   2. LÍNEA DE PROGRESO & CÍRCULOS
   ========================================= */
.steps-container {
  max-width: 600px;
  margin: 0 auto 3rem auto;
  position: relative;
  padding: 0 10px; /* Margen de seguridad para los círculos extremos */
}

.steps-line {
  display: flex;
  justify-content: space-between; /* Distribuye círculos uniformemente */
  align-items: center;
  position: relative;
}

/* Fondo de la línea (Gris) */
.progress-track {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-50%);
  z-index: 1;
  border-radius: 4px;
}

/* Relleno de la línea (Rayo Eléctrico) */
.progress-fill {
  position: absolute;
  top: 50%;
  left: 0;
  height: 4px;
  background: linear-gradient(90deg, #a8ff78, #78ffd6);
  box-shadow: 0 0 10px #78ffd6, 0 0 20px #a8ff78; /* Doble brillo */
  transform: translateY(-50%);
  z-index: 2;
  border-radius: 4px;
  transition: width 0.5s cubic-bezier(0.25, 1, 0.5, 1); /* Efecto suave pero rápido */
}

/* Estilo Base de los Círculos */
.step-circle {
  width: 50px;
  height: 50px;
  background: #1f3c4d; /* Mismo color que el fondo para "cortar" la línea */
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3; /* Encima de las líneas */
  cursor: pointer;
  font-weight: bold;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;
}

/* Círculos por los que ya pasó el rayo */
.step-circle.active {
  border-color: #78ffd6;
  color: #78ffd6;
  box-shadow: 0 0 10px rgba(120, 255, 214, 0.3);
}

/* Círculo actual (Más grande y brillante) */
.step-circle.current {
  transform: scale(1.2);
  background: #1f3c4d;
  color: #fff;
  border-color: #a8ff78;
  box-shadow: 0 0 20px rgba(168, 255, 120, 0.6);
}

/* =========================================
   3. CARD (ESTILO GLASS / ELECTRIC)
   ========================================= */
.card-display-area {
  position: relative;
  width: 100%;
  max-width: 950px;
  margin: 0 auto;
  min-height: 220px; /* Altura mínima para evitar saltos */
  perspective: 1000px;
}

.single-card {
  /* GLASSMORPHISM */
  background: linear-gradient(
    145deg, 
    rgba(255, 255, 255, 0.1) 0%, 
    rgba(255, 255, 255, 0.02) 100%
  );
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  
  /* BORDES ELÉCTRICOS SUTILES */
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  border-left: 1px solid rgba(255, 255, 255, 0.3);
  
  /* SOMBRA Y FORMA */
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.3);
  border-radius: 1.5rem;
  padding: 2.5rem;
  
  /* POSICIONAMIENTO */
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  text-align: left;
}

.card-title {
  font-size: 1.8rem;
  margin-bottom: 1rem;
  /* Texto degradado opcional */
  background: linear-gradient(90deg, #fff, #aee2ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
}

.card-text {
  font-size: 1.1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
}

/* =========================================
   4. TRANSICIONES (Fade + Slide Up)
   ========================================= */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s ease-out;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(20px); /* Entra desde abajo */
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px); /* Sale hacia arriba */
}

/* =========================================
   5. RESPONSIVE
   ========================================= */
@media (max-width: 600px) {
  .steps-container {
    max-width: 100%;
  }
  
  .step-circle {
    width: 40px;
    height: 40px;
    font-size: 0.9rem;
  }
  
  .single-card {
    padding: 1.5rem;
    border-radius: 1rem;
  }
  
  .card-title {
    font-size: 1.5rem;
  }
}
</style>